// marketStatusOverlay.ts — fetches per-market status overlay from
// Supabase public.markets to merge onto the Flask-served cockpit
// markets list. Per DECISIONS.md 2026-05-17 I-065.
//
// The cockpit's display source is Flask's hardcoded seed list
// (engine/app.py:1500+); Arena Supabase holds the parimutuel
// engine's source of truth for status / settled_at / winning_outcome.
// This overlay is the third data path that lets the cockpit show
// SETTLED state from Supabase against rows it learned about from
// Flask. The architecturally clean alternative (cockpit reads
// markets directly from Supabase via public.cockpit_markets view)
// is captured as IDEAS I-066, deferred.
//
// Fail-open contract: Supabase disabled or transport error → empty
// Map returned. Cockpit falls back to Flask-only data; no blank
// feed if Arena is unreachable.

import { supabase, isSupabaseEnabled } from './supabaseClient';

export type MarketStatus = 'OPEN' | 'SETTLED' | 'CLOSED' | 'CANCELLED' | string;

// I-068 Brief 2: the foreign-table select on oracle_sources returns a
// nested object when primary_oracle_id is populated, and null when it
// isn't (system-audit and any future internal-test rows). Card and
// focus-modal render code must handle the null case gracefully.
export interface OracleInfo {
  id:                       string;
  display_name:             string;
  oracle_class:             number;
  pledger_disclosure_text:  string;
  default_holdback_hours:   number;
}

export interface MarketStatusOverlay {
  status:           MarketStatus;
  settled_at:       string | null;   // ISO timestamp from Supabase, null when not yet settled
  winning_outcome:  string | null;
  // I-068 Brief 2 fields. `oracle` is the joined oracle_sources row;
  // `holdback_hours` is the per-market override (null = use registry
  // default). `auto_settle` is the load-bearing flag for I-070 and
  // surfaced here for completeness; not currently rendered.
  oracle:           OracleInfo | null;
  auto_settle:      boolean;
  holdback_hours:   number | null;
  // Added 2026-05-20 for the LOCAL feed sort control. The Flask seed
  // response does not carry challenge_deadline (only created_at does);
  // Supabase's public.markets row is the authoritative source. Pulled
  // into the overlay so the cockpit can sort by nearest closure.
  challenge_deadline: string | null;
}

export async function fetchMarketStatusOverlay(
  marketIds: string[],
): Promise<Map<string, MarketStatusOverlay>> {
  const empty = new Map<string, MarketStatusOverlay>();
  if (!isSupabaseEnabled() || !supabase || marketIds.length === 0) return empty;

  // Foreign-table select on oracle_sources!primary_oracle_id is the
  // supabase-js idiomatic answer for the one-to-one join. PostgREST
  // returns the joined row as an object (or null for unlinked
  // markets). Verified shape via anon probe 2026-05-19 against all
  // four representative cases (Class 1 with holdback override,
  // Class 1 with no override, Class 3, system-audit/null).
  const { data, error } = await supabase
    .from('markets')
    .select(`
      id, status, settled_at, winning_outcome,
      auto_settle, holdback_hours, challenge_deadline,
      oracle:oracle_sources!primary_oracle_id (
        id, display_name, oracle_class, pledger_disclosure_text, default_holdback_hours
      )
    `)
    .in('id', marketIds);

  if (error || !data) return empty;

  const out = new Map<string, MarketStatusOverlay>();
  for (const row of data) {
    // supabase-js types the foreign-table select as an array even for
    // one-to-one relations; flatten to a single object or null.
    const rawOracle = (row as { oracle?: OracleInfo | OracleInfo[] | null }).oracle;
    const oracle: OracleInfo | null = Array.isArray(rawOracle)
      ? (rawOracle[0] ?? null)
      : (rawOracle ?? null);

    out.set(row.id, {
      status:             row.status,
      settled_at:         row.settled_at,
      winning_outcome:    row.winning_outcome,
      oracle,
      auto_settle:        row.auto_settle ?? false,
      holdback_hours:     row.holdback_hours,
      challenge_deadline: (row as { challenge_deadline?: string | null }).challenge_deadline ?? null,
    });
  }
  return out;
}
